// The site plan around the building: how cars and people get in, and the
// territorial balance (bilanț teritorial) of what the plot is covered with.
//
// Green space is what is left once the building, the open part of the ramp
// and the footpaths are drawn. Part of it sits over the basement: green on a
// slab is not green on natural ground, and local regulations often ask for a
// share of the latter, so both are reported.
import { area, capsule, difference, edges, intersection, union, type MultiPoly, type Pt } from "./geom.ts";
import type { Envelope } from "./envelope.ts";
import type { Layout } from "./layout.ts";
import type { Option } from "./massing.ts";
import type { Parking } from "./programme.ts";
import type { Site } from "./rules.ts";

export const PATH_WIDTH = 1.8;

export interface SitePlan {
  /** Where the car access crosses the street boundary. */
  vehicleAccess?: Pt;
  /** The ramp where it is open to the sky, between the street and the building. */
  rampOpen: MultiPoly;
  /** Footpaths from each entrance to the street. */
  paths: MultiPoly;
  pedestrianAccess: Pt[];
  green: MultiPoly;
  /** Surface car park: stalls and aisles at grade. */
  surface: MultiPoly;
  balance: { plot: number; built: number; paved: number; green: number; greenNatural: number };
}

function nearestOn(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
  return [a[0] + t * dx, a[1] + t * dy];
}

/** Footpaths: each entrance straight to the nearest point of the street. */
export function footpaths(env: Envelope, site: Site, l: Layout): MultiPoly {
  const entrances = l.floors[0].units.map((u) => u.entrance).filter((p): p is Pt => !!p);
  if (!entrances.length) return [];
  const toStreet = streetPoint(env, site);
  return intersection(union(...entrances.map((e) => capsule(e, toStreet(e), PATH_WIDTH / 2, 4))), env.plot);
}

function streetPoint(env: Envelope, site: Site) {
  const ring = env.plot[0][0];
  const street = edges(ring).filter((_, i) => site.edges[i] === "aliniament");
  return (p: Pt): Pt => {
    const candidates = (street.length ? street : edges(ring)).map(([a, b]) => nearestOn(p, a, b));
    return candidates.reduce((best, q) => (Math.hypot(q[0] - p[0], q[1] - p[1]) < Math.hypot(best[0] - p[0], best[1] - p[1]) ? q : best));
  };
}

export function sitePlan(env: Envelope, site: Site, o: Option, l: Layout, p: Parking): SitePlan {
  const parking = p.plan;
  const ground = o.storeys[0].footprint;
  const toStreet = streetPoint(env, site);
  const lv = parking.levels[0];
  const rampOpen = lv ? difference(intersection(lv.ramp, env.plot), ground) : [];
  const surface = union(...p.surfaceStalls, p.surfaceAisles);
  // cars come in down the ramp, or onto the surface car park, or into the podium
  const podium = l.floors[0].units.find((u) => u.kind === "parking");
  const target = lv && lv.ramp.length ? lv.ramp : surface.length ? surface : podium?.polygon;
  let vehicleAccess: Pt | undefined;
  if (target && target.length) {
    const pts = target.flatMap((q) => q[0]);
    vehicleAccess = toStreet([pts.reduce((t, q) => t + q[0], 0) / pts.length, pts.reduce((t, q) => t + q[1], 0) / pts.length]);
  }
  const entrances = l.floors[0].units.map((u) => u.entrance).filter((q): q is Pt => !!q);
  const pedestrianAccess = entrances.map(toStreet);
  const paths = difference(footpaths(env, site, l), ground, rampOpen);
  const paved = union(rampOpen, paths, surface);
  const green = difference(env.plot, ground, paved);
  const basement = lv ? lv.footprint : [];
  const balance = {
    plot: env.plotArea,
    built: area(ground),
    paved: area(difference(paved, ground)),
    green: area(green),
    greenNatural: area(difference(green, basement)),
  };
  return { vehicleAccess, rampOpen, paths, pedestrianAccess, green, surface, balance };
}
